import React, { useRef } from 'react';
import { Row, Col, Image, Alert } from "react-bootstrap";
import Container from '../components/Container';
import SectionHeader from '../components/SectionHeader';
import { Calendar, ExternalLink } from 'lucide-react';
import { useActualites } from '../context/ActualitesContext';
import { motion, useInView, useAnimation } from 'framer-motion';
import '../styles/components.css';

const Actualite = () => {
  const { actualites, loading } = useActualites();

  const articlesRef = useRef(null);
  const ctaRef = useRef(null);

  const articlesInView = useInView(articlesRef, { once: true, margin: "-100px" });
  const ctaInView = useInView(ctaRef, { once: true, margin: "-100px" });

  const articlesControls = useAnimation();
  const ctaControls = useAnimation();

  React.useEffect(() => {
    if (articlesInView) articlesControls.start("visible");
  }, [articlesInView, articlesControls]);

  React.useEffect(() => {
    if (ctaInView) ctaControls.start("visible");
  }, [ctaInView, ctaControls]);

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.2 }
    }
  };

  const staggerItem = {
    hidden: { opacity: 0, y: 30, scale: 0.95 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: { duration: 0.5, ease: "easeOut" }
    }
  };

  const fadeInUp = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" }
    }
  };

  return (
    <div className="section" style={{ overflow: 'hidden' }}>
      <Container>
        <motion.div
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <SectionHeader 
            title="Actualités"
            subtitle="Restez informé des dernières actualités et événements liés à l'environnement et au développement durable"
          />
        </motion.div>

        {loading ? (
          <motion.p 
            style={{ textAlign: 'center', color: 'var(--color-text-light)' }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            Chargement des actualités...
          </motion.p>
        ) : actualites.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
          >
            <Alert variant="info" style={{ textAlign: 'center' }}>
              Aucune actualité disponible pour le moment.
            </Alert>
          </motion.div>
        ) : (
          <motion.div
            ref={articlesRef}
            initial="hidden"
            animate={articlesControls}
            variants={staggerContainer}
          >
            <Row className="g-4" style={{ marginTop: 'var(--spacing-3xl)' }}>
              {actualites.map((actualite) => (
                <Col key={actualite.id} lg={4} md={6}>
                  <motion.div
                    variants={staggerItem}
                    whileHover={{ 
                      y: -8,
                      transition: { duration: 0.3 }
                    }}
                  >
                    <div className="card-modern" style={{ 
                      height: '100%', 
                      display: 'flex', 
                      flexDirection: 'column',
                      transition: 'all 0.3s ease'
                    }}>
                      {actualite.image && (
                        <motion.div 
                          style={{ 
                            marginBottom: 'var(--spacing-lg)', 
                            borderRadius: 'var(--border-radius-md)', 
                            overflow: 'hidden' 
                          }}
                          whileHover={{ scale: 1.05 }}
                          transition={{ duration: 0.3 }}
                        >
                          <Image 
                            src={actualite.image} 
                            fluid 
                            style={{ 
                              width: '100%', 
                              height: '200px', 
                              objectFit: 'cover',
                              display: 'block'
                            }}
                          />
                        </motion.div>
                      )}
                      <div style={{ flexGrow: 1 }}>
                        <div style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          marginBottom: 'var(--spacing-md)',
                          fontSize: 'var(--font-size-sm)',
                          color: 'var(--color-text-light)'
                        }}>
                          <Calendar size={16} style={{ marginRight: '0.5rem', color: 'var(--color-primary)' }} />
                          <span>{actualite.date}</span>
                          {actualite.location && (
                            <>
                              <span style={{ margin: '0 0.5rem' }}>•</span>
                              <span>{actualite.location}</span>
                            </>
                          )}
                        </div>
                        <h3 className="card-title" style={{ marginBottom: 'var(--spacing-md)' }}>
                          {actualite.title}
                        </h3>
                        <p className="card-text" style={{ marginBottom: 'var(--spacing-lg)' }}>
                          {actualite.description}
                        </p>
                      </div>
                      {actualite.link && (
                        <motion.a 
                          href={actualite.link} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            color: 'var(--color-primary)',
                            textDecoration: 'none',
                            fontWeight: 'var(--font-weight-medium)',
                            fontSize: 'var(--font-size-sm)',
                            transition: 'var(--transition-base)'
                          }}
                          whileHover={{ 
                            color: 'var(--color-accent)',
                            x: 5
                          }}
                          whileTap={{ scale: 0.95 }}
                        >
                          Lire la suite
                          <ExternalLink size={16} style={{ marginLeft: '0.5rem' }} />
                        </motion.a>
                      )}
                    </div>
                  </motion.div>
                </Col>
              ))}
            </Row>
          </motion.div>
        )}

        <motion.div
          ref={ctaRef}
          initial="hidden"
          animate={ctaControls}
          variants={fadeInUp}
        >
          <Row style={{ marginTop: 'var(--spacing-4xl)' }}>
            <Col lg={10} className="mx-auto">
              <motion.div 
                className="card-modern"
                whileHover={{ 
                  scale: 1.02,
                  boxShadow: 'var(--shadow-xl)'
                }}
                transition={{ duration: 0.3 }}
                style={{
                  background: 'linear-gradient(135deg, rgba(15, 118, 110, 0.05) 0%, rgba(2, 132, 199, 0.05) 100%)',
                  padding: 'var(--spacing-3xl)',
                  textAlign: 'center'
                }}
              >
                <h3 style={{ 
                  fontSize: 'var(--font-size-3xl)', 
                  fontWeight: 'var(--font-weight-semibold)',
                  marginBottom: 'var(--spacing-lg)',
                  color: 'var(--color-text)'
                }}>
                  Restez informé
                </h3>
                <p style={{ 
                  fontSize: 'var(--font-size-lg)', 
                  lineHeight: 'var(--line-height-relaxed)',
                  color: 'var(--color-text-light)',
                  marginBottom: 'var(--spacing-xl)'
                }}>
                  Suivez nos actualités et événements pour ne rien manquer de nos actions en faveur de l'environnement.
                </p>
                <motion.a 
                  href="/contact" 
                  className="btn-modern btn-modern-primary btn-modern-lg"
                  style={{ textDecoration: 'none', display: 'inline-block' }}
                  whileHover={{ scale: 1.05, boxShadow: 'var(--shadow-lg)' }}
                  whileTap={{ scale: 0.95 }}
                >
                  Nous contacter
                </motion.a>
              </motion.div>
            </Col>
          </Row>
        </motion.div>
      </Container>
    </div>
  );
};

export default Actualite;
